import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Calendar, User, Clock, Eye, 
  ArrowLeft, Grid, List, Brain,
  BookOpen, Menu, X, LogOut,
  ChevronRight
} from 'lucide-react';
import { useLearningArticles } from '../hooks/useLearningArticles';
import { useAuth } from '../contexts/AuthContext';
import UnifiedNavigation from '../components/common/UnifiedNavigation';
import BreadcrumbNavigation from '../components/common/BreadcrumbNavigation';
import SEOHead from '../components/common/SEOHead';
import SkeletonLoader from '../components/common/SkeletonLoader';

interface FilterOptions {
  sortBy: 'latest' | 'popular' | 'title';
  viewMode: 'grid' | 'list';
  subjectId: string; // 思维学科筛选
}

const LearningArticleListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [filters, setFilters] = useState<FilterOptions>({
    sortBy: (searchParams.get('sortBy') as FilterOptions['sortBy']) || 'latest',
    viewMode: (searchParams.get('viewMode') as FilterOptions['viewMode']) || 'grid',
    subjectId: searchParams.get('subjectId') || ''
  });

  // 将前端排序选项映射到后端排序字段
  const getSortParams = (sortBy: FilterOptions['sortBy']) => {
    switch (sortBy) {
      case 'popular':
        return { sortBy: 'read_count' as const, sortOrder: 'desc' as const };
      case 'title':
        return { sortBy: 'title' as const, sortOrder: 'asc' as const };
      case 'latest':
      default:
        return { sortBy: 'created_at' as const, sortOrder: 'desc' as const };
    }
  };

  const sortParams = getSortParams(filters.sortBy);

  const { data: articles, error, isLoading } = useLearningArticles({
    limit: 50,
    subject_id: filters.subjectId || undefined,
    sortBy: sortParams.sortBy,
    sortOrder: sortParams.sortOrder
  });

  // 更新URL参数
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.sortBy !== 'latest') params.set('sortBy', filters.sortBy);
    if (filters.viewMode !== 'grid') params.set('viewMode', filters.viewMode);
    if (filters.subjectId) params.set('subjectId', filters.subjectId);
    
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // 使用后端已排序的数据，无需前端再次排序
  const filteredArticles = articles || [];

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // 估算阅读时间
  const estimateReadingTime = (content: string) => {
    if (!content) return 5;
    return Math.ceil(content.length / 300) || 5;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">加载失败</h2>
          <p className="text-gray-600 mb-4">无法加载学习文章数据，请稍后重试。</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="思维训练文章列表 - LeapWard深度思考平台"
        description="浏览所有思维训练文章，系统性学习思维方法和认知技巧，提升思维能力。"
        keywords="思维训练,学习文章,认知提升,LeapWard,思维方法,思维能力"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* 顶部导航栏 */}
        <UnifiedNavigation currentPage="learning" />

        {/* 页面内容区域 */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* 面包屑导航 */}
          <BreadcrumbNavigation 
            items={[
              { name: '思维训练', current: true }
            ]}
            className="mb-6"
          />

          {/* 页面标题和统计 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                思维训练
                {filters.subjectId && (
                  <span className="text-blue-600 ml-2">
                    - {filters.subjectId === '1' ? '批判性思维' :
                        filters.subjectId === '4' ? '系统化思维' :
                        filters.subjectId === '3' ? '创新思维' : ''}
                  </span>
                )}
              </h1>
              <p className="text-gray-600">
                {isLoading ? '加载中...' : (
                  <>
                    {filters.subjectId ? (
                      `${filters.subjectId === '1' ? '批判性思维' :
                         filters.subjectId === '4' ? '系统化思维' :
                         filters.subjectId === '3' ? '创新思维' : '选定学科'} 共 ${filteredArticles.length} 篇学习文章`
                    ) : (
                      `共 ${filteredArticles.length} 篇学习文章`
                    )}
                  </>
                )}
              </p>
            </div>
            
            {/* 视图切换 */}
            <div className="flex items-center space-x-2 mt-4 sm:mt-0">
              <button
                onClick={() => handleFilterChange('viewMode', 'grid')}
                className={`p-2 rounded-lg transition-colors ${
                  filters.viewMode === 'grid'
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleFilterChange('viewMode', 'list')}
                className={`p-2 rounded-lg transition-colors ${
                  filters.viewMode === 'list'
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* 思维学科筛选按钮 - 固定显示 */}
          <div className="bg-white rounded-lg shadow-sm border mb-6 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">思维学科</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleFilterChange('subjectId', '')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filters.subjectId === ''
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    全部
                  </button>
                  <button
                    onClick={() => handleFilterChange('subjectId', '1')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filters.subjectId === '1'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    批判性思维
                  </button>
                  <button
                    onClick={() => handleFilterChange('subjectId', '4')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filters.subjectId === '4'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    系统化思维
                  </button>
                  <button
                    onClick={() => handleFilterChange('subjectId', '3')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filters.subjectId === '3'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    创新思维
                  </button>
                </div>
              </div>
              
              {/* 排序选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  排序方式
                </label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value as FilterOptions['sortBy'])}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="latest">最新发布</option>
                  <option value="popular">最受欢迎</option>
                  <option value="title">标题排序</option>
                </select>
              </div>
            </div>
          </div>

          {/* 文章列表 */}
          {isLoading ? (
            <div className={filters.viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
            }>
              {[...Array(6)].map((_, index) => (
                <SkeletonLoader key={index} className="h-64" />
              ))}
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 text-6xl mb-4">🧠</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                暂无学习文章
              </h3>
              <p className="text-gray-500 mb-4">
                精彩的思维训练内容即将上线，敬请期待
              </p>
            </div>
          ) : (
            <div className={filters.viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
            }>
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/learning/article/${article.id}`}
                  className={`bg-white rounded-lg shadow-sm hover:shadow-xl hover:border-green-300 hover:-translate-y-1 transition-all duration-300 border block group cursor-pointer ${
                    filters.viewMode === 'list' ? 'p-6' : 'overflow-hidden'
                  }`}
                >
                  {filters.viewMode === 'grid' ? (
                    // 网格视图
                    <>
                      {/* 顶部渐变条 */}
                      <div className="h-1 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500"></div>
                      
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-blue-600 rounded flex items-center justify-center">
                              <Brain className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-sm text-green-600 font-medium">
                              思维训练
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {formatDate(article.created_at)}
                          </span>
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-green-600 transition-colors duration-200">
                          {article.title}
                        </h3>
                        
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {article.summary || '通过系统性学习，提升思维认知能力'}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                          <div className="flex items-center space-x-2">
                            <BookOpen className="w-3 h-3" />
                            <span>学习文章</span>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <span className="flex items-center">
                              <Eye className="w-3 h-3 mr-1" />
                              {article.read_count || 0}
                            </span>
                            <span className="px-2 py-1 bg-gradient-to-r from-blue-100 to-green-100 text-blue-800 rounded-full font-semibold">
                              {estimateReadingTime(article.content)}分钟阅读
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    // 列表视图
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <Brain className="w-8 h-8 text-white" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm text-green-600 font-medium">
                              思维训练
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {formatDate(article.created_at)}
                            </span>
                          </div>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors duration-200">
                          {article.title}
                        </h3>
                        
                        <p className="text-gray-600 mb-3 line-clamp-2">
                          {article.summary || '通过系统性学习，提升思维认知能力'}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <BookOpen className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-500">
                                学习文章
                              </span>
                            </div>
                            <span className="flex items-center text-sm text-gray-500">
                              <Eye className="w-4 h-4 mr-1" />
                              {article.read_count || 0} 阅读
                            </span>
                          </div>
                          
                          <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-green-100 text-blue-800 rounded-full text-sm font-semibold">
                            {estimateReadingTime(article.content)}分钟阅读
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        {!user && filteredArticles.length > 0 && (
          <div className="bg-green-50 border-t">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  想要获得更深入的学习体验？
                </h3>
                <p className="text-gray-600 mb-4">
                  注册账号，解锁完整的学习功能、个人进度跟踪和学习成果记录
                </p>
                <button
                  onClick={() => navigate('/', { state: { scrollTo: 'hero' } })}
                  className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  立即注册
                  <Brain className="ml-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LearningArticleListPage;